import React, { useState, useEffect } from 'react';
import {
  Users, ChevronDown, ChevronUp, Edit2, Save, X,
  CheckCircle, Clock, AlertCircle, RefreshCw, Mail,
  Phone, FileText, Shield,
  Loader, Plus, FolderOpen,
  DollarSign, Calendar, Trash2
} from 'lucide-react';
import {
  collection, query, where, getDocs, doc, updateDoc,
  orderBy, Timestamp, writeBatch, deleteDoc, onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase';
import { createProject } from '../../services/projectService';
import { STORAGE_LIMITS } from '../../services/storageService';
import './ClientsManager.css';

const ClientsManager = ({ currentUser }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClient, setExpandedClient] = useState(null);
  const [activeTab, setActiveTab] = useState({});
  const [clientDetails, setClientDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [editingClient, setEditingClient] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [createProjectModal, setCreateProjectModal] = useState(null);
  const [newProjectData, setNewProjectData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (clientId, section) => {
    const key = clientId + '_' + section;
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isSectionOpen = (clientId, section) => !!collapsedSections[clientId + '_' + section];
  const [viewingRequest, setViewingRequest] = useState(null); // budget request detail modal
  const [newRequestAlert, setNewRequestAlert] = useState(null); // { type: 'budget'|'project', clientName, name }

  useEffect(() => {
    loadClients();

    // Listen for new budget requests and projects to show alert
    const mountTime = new Date();
    const reqUnsub = onSnapshot(
      query(collection(db, 'budgetRequests'), where('status', '==', 'pending'), orderBy('createdAt', 'desc')),
      (snap) => {
        snap.docChanges().forEach(change => {
          if (change.type === 'added') {
            const data = change.doc.data();
            const createdAt = data.createdAt?.toDate?.();
            if (createdAt && createdAt > mountTime) {
              setNewRequestAlert({ type: 'budget', clientName: data.clientName || 'A client', name: data.name });
              // Refresh client details if open
              setClientDetails(prev => {
                const n = { ...prev };
                delete n[data.clientId];
                return n;
              });
            }
          }
        });
      }
    );

    return () => reqUnsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClients = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'client'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClientDetails = async (clientId) => {
    if (clientDetails[clientId]) return;
    setLoadingDetails(prev => ({ ...prev, [clientId]: true }));
    try {
      const [budgetSnap, projectSnap] = await Promise.all([
        getDocs(query(collection(db, 'budgetRequests'), where('clientId', '==', clientId), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'projects'), where('clientId', '==', clientId), orderBy('createdAt', 'desc')))
      ]);
      setClientDetails(prev => ({
        ...prev,
        [clientId]: {
          budgetRequests: budgetSnap.docs.map(d => ({ id: d.id, ...d.data() })),
          projects: projectSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        }
      }));
    } catch (error) {
      console.error('Error loading client details:', error);
    } finally {
      setLoadingDetails(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const toggleClient = async (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
      return;
    }
    setExpandedClient(clientId);
    if (!activeTab[clientId]) setActiveTab(prev => ({ ...prev, [clientId]: 'profile' }));
    loadClientDetails(clientId);
  };

  const handleEditClient = (client) => {
    setEditingClient(client.id);
    setEditData({
      displayName: client.displayName || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      website: client.website || '',
      businessName: client.businessName || '',
      nif: client.nif || '',
      currency: client.currency || 'EUR',
      language: client.language || 'es',
      timezone: client.timezone || 'Europe/Madrid',
      status: client.status || 'approved',
      storageLimit: client.storageLimit || STORAGE_LIMITS['1GB'],
      notes: client.notes || '',
      permissions: {
        canEditMilestones: client.permissions?.canEditMilestones || false,
        canEditBudgets: client.permissions?.canEditBudgets || false,
      }
    });
  };

  const handleSaveClient = async (clientId) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', clientId), {
        displayName: editData.displayName,
        phone: editData.phone,
        address: editData.address,
        website: editData.website,
        businessName: editData.businessName,
        nif: editData.nif,
        currency: editData.currency,
        language: editData.language,
        timezone: editData.timezone,
        status: editData.status,
        storageLimit: parseInt(editData.storageLimit),
        notes: editData.notes,
        permissions: editData.permissions,
        updatedAt: Timestamp.now()
      });
      setClients(prev => prev.map(c =>
        c.id === clientId ? { ...c, ...editData } : c
      ));
      setEditingClient(null);
    } catch (error) {
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Delete client "${client.displayName || client.email}"?\n\nThis will delete their profile, messages, files metadata, budget requests and projects from the database. This cannot be undone.`)) return;

    try {
      const batch = writeBatch(db);

      // Delete messages
      const messagesSnap = await getDocs(query(collection(db, 'messages'), where('chatId', '==', client.id)));
      messagesSnap.docs.forEach(d => batch.delete(d.ref));

      // Delete budget requests
      const requestsSnap = await getDocs(query(collection(db, 'budgetRequests'), where('clientId', '==', client.id)));
      requestsSnap.docs.forEach(d => batch.delete(d.ref));

      // Delete projects and their milestones
      const projectsSnap = await getDocs(query(collection(db, 'projects'), where('clientId', '==', client.id)));
      for (const projectDoc of projectsSnap.docs) {
        const milestonesSnap = await getDocs(query(collection(db, 'milestones'), where('projectId', '==', projectDoc.id)));
        milestonesSnap.docs.forEach(d => batch.delete(d.ref));
        batch.delete(projectDoc.ref);
      }

      // Delete files metadata
      const filesSnap = await getDocs(query(collection(db, 'files'), where('clientId', '==', client.id)));
      filesSnap.docs.forEach(d => batch.delete(d.ref));

      // Delete user document
      batch.delete(doc(db, 'users', client.id));

      await batch.commit();

      setClients(prev => prev.filter(c => c.id !== client.id));
      if (expandedClient === client.id) setExpandedClient(null);
    } catch (error) {
      alert('Error deleting client: ' + error.message);
    }
  };

  const handleClearChat = async (clientId) => {
    if (!window.confirm('Delete all messages for this client? This cannot be undone.')) return;
    try {
      const snap = await getDocs(query(collection(db, 'messages'), where('chatId', '==', clientId)));
      const batch = writeBatch(db);
      snap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
      alert('Chat cleared successfully.');
    } catch (error) {
      alert('Error clearing chat: ' + error.message);
    }
  };

  const handleBudgetRequestAction = async (clientId, requestId, newStatus, reason = '') => {
    try {
      const updates = { status: newStatus };
      if (reason) updates.rejectionReason = reason;
      if (newStatus === 'approved') updates.approvedAt = Timestamp.now();
      await updateDoc(doc(db, 'budgetRequests', requestId), updates);
      setClientDetails(prev => ({
        ...prev,
        [clientId]: {
          ...prev[clientId],
          budgetRequests: prev[clientId].budgetRequests.map(r =>
            r.id === requestId ? { ...r, ...updates } : r
          )
        }
      }));
      setRejectModal(null);
      setRejectReason('');
    } catch (error) {
      alert('Error updating request: ' + error.message);
    }
  };

  const handleDeleteBudgetRequest = async (clientId, requestId) => {
    if (!window.confirm('Delete this budget request?')) return;
    try {
      await deleteDoc(doc(db, 'budgetRequests', requestId));
      setClientDetails(prev => ({
        ...prev,
        [clientId]: {
          ...prev[clientId],
          budgetRequests: prev[clientId].budgetRequests.filter(r => r.id !== requestId)
        }
      }));
      if (viewingRequest?.id === requestId) setViewingRequest(null);
    } catch (error) {
      alert('Error deleting request: ' + error.message);
    }
  };

  const handleCreateProjectFromRequest = async (client, request) => {
    setCreateProjectModal({ client, request });
    setNewProjectData({
      name: request.name || '',
      description: request.description || '',
      budget: request.budget || '',
      startDate: '',
      endDate: request.deadline ? formatDateInput(request.deadline) : '',
    });
  };

  const confirmCreateProject = async () => {
    const { client, request } = createProjectModal;
    setSaving(true);
    try {
      const result = await createProject({
        name: newProjectData.name,
        description: newProjectData.description,
        budget: newProjectData.budget ? parseFloat(newProjectData.budget) : null,
        startDate: newProjectData.startDate ? new Date(newProjectData.startDate) : null,
        endDate: newProjectData.endDate ? new Date(newProjectData.endDate) : null,
        clientId: client.id,
        clientName: client.displayName || client.email,
      });
      if (result.success) {
        await handleBudgetRequestAction(client.id, request.id, 'approved');
        setCreateProjectModal(null);
        // Reload details
        setClientDetails(prev => { const n = { ...prev }; delete n[client.id]; return n; });
        loadClientDetails(client.id);
      } else {
        alert('Error: ' + result.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateInput = (timestamp) => {
    if (!timestamp) return '';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return d.toISOString().split('T')[0];
  };

  const formatStorage = (bytes) => {
    if (!bytes) return '0 MB';
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
    return (bytes / 1048576).toFixed(0) + ' MB';
  };

  const getStatusBadge = (status) => {
    const cfg = {
      approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7' },
      pending: { label: 'Pending', color: '#d97706', bg: '#fef3c7' },
      suspended: { label: 'Suspended', color: '#dc2626', bg: '#fee2e2' },
    };
    const c = cfg[status] || cfg.pending;
    return <span className="status-badge" style={{ color: c.color, background: c.bg }}>{c.label}</span>;
  };

  const getBudgetStatusBadge = (status) => {
    const cfg = {
      pending: { label: 'Pending', color: '#d97706', bg: '#fef3c7', icon: <Clock size={12} /> },
      reviewing: { label: 'Reviewing', color: '#2563eb', bg: '#dbeafe', icon: <RefreshCw size={12} /> },
      approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7', icon: <CheckCircle size={12} /> },
      rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', icon: <AlertCircle size={12} /> },
      cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6', icon: <X size={12} /> },
    };
    const c = cfg[status] || cfg.pending;
    return (
      <span className="budget-status-badge" style={{ color: c.color, background: c.bg }}>
        {c.icon}{c.label}
      </span>
    );
  };

  const filteredClients = clients.filter(c =>
    !searchTerm ||
    c.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="clients-loading">
        <Loader className="spinner" size={32} />
        <p>Loading clients...</p>
      </div>
    );
  }

  return (
    <div className="clients-manager">
      <div className="clients-header">
        <div>
          <h1><Users size={24} />Clients</h1>
          <p>Manage clients, permissions, and budget requests</p>
        </div>
        <div className="clients-count">{clients.length} clients</div>
      </div>

      <div className="clients-search">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="clients-list">
        {filteredClients.length === 0 ? (
          <div className="clients-empty">
            <Users size={48} />
            <p>No clients found</p>
          </div>
        ) : (
          filteredClients.map(client => {
            const isExpanded = expandedClient === client.id;
            const details = clientDetails[client.id];
            const isLoading = loadingDetails[client.id];
            const tab = activeTab[client.id] || 'profile';
            const isEditing = editingClient === client.id;
            const pendingRequests = details?.budgetRequests?.filter(r => r.status === 'pending').length || 0;
            const activeProject = details?.projects?.find(p => p.status === 'active');

            return (
              <div key={client.id} className={`client-card ${isExpanded ? 'expanded' : ''}`}>
                {/* Header */}
                <div className="client-card-header" onClick={() => toggleClient(client.id)}>
                  <div className="client-card-left">
                    {client.photoURL ? (
                      <img src={client.photoURL} alt={client.displayName} className="client-avatar" />
                    ) : (
                      <div className="client-avatar-placeholder">
                        {(client.displayName || client.email || '?')[0].toUpperCase()}
                      </div>
                    )}
                    <div className="client-card-info">
                      <div className="client-card-name">
                        {client.displayName || client.email}
                        {getStatusBadge(client.status)}
                      </div>
                      <div className="client-card-meta">
                        <span><Mail size={13} />{client.email}</span>
                        {client.phone && <span><Phone size={13} />{client.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="client-card-right">
                    {pendingRequests > 0 && (
                      <span className="pending-badge">{pendingRequests} request{pendingRequests > 1 ? 's' : ''}</span>
                    )}
                    {activeProject && (
                      <span className="active-project-badge"><FolderOpen size={13} />Active project</span>
                    )}
                    <button className="expand-btn">
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                </div>

                {/* Expanded */}
                {isExpanded && (
                  <div className="client-card-body">
                    <div className="client-tabs">
                      <button
                        className={`client-tab ${tab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab(prev => ({ ...prev, [client.id]: 'profile' }))}
                      >
                        <Shield size={15} />Profile & Permissions
                      </button>
                      <button
                        className={`client-tab ${tab === 'activity' ? 'active' : ''}`}
                        onClick={() => setActiveTab(prev => ({ ...prev, [client.id]: 'activity' }))}
                      >
                        <FolderOpen size={15} />Activity
                        {pendingRequests > 0 && <span className="tab-badge">{pendingRequests}</span>}
                      </button>
                    </div>

                    {isLoading ? (
                      <div className="details-loading">
                        <Loader className="spinner" size={20} />
                      </div>
                    ) : (
                      <>
                        {/* PROFILE TAB */}
                        {tab === 'profile' && (
                          <div className="profile-tab">
                            {!isEditing ? (
                              <>
                                <div className="profile-sections">
                                  <div className="profile-section">
                                    <h4>Personal Info</h4>
                                    <div className="profile-grid">
                                      <div className="profile-field">
                                        <span className="field-label">Name</span>
                                        <span className="field-value">{client.displayName || '—'}</span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Email</span>
                                        <span className="field-value">{client.email || '—'}</span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Phone</span>
                                        <span className="field-value">{client.phone || '—'}</span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Website</span>
                                        <span className="field-value">{client.website || '—'}</span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Address</span>
                                        <span className="field-value">{client.address || '—'}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="profile-section">
                                    <h4>Billing</h4>
                                    <div className="profile-grid">
                                      <div className="profile-field">
                                        <span className="field-label">Business Name</span>
                                        <span className="field-value">{client.businessName || '—'}</span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">NIF/CIF</span>
                                        <span className="field-value">{client.nif || '—'}</span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Currency</span>
                                        <span className="field-value">{client.currency || '—'}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="profile-section">
                                    <h4>Permissions & Storage</h4>
                                    <div className="profile-grid">
                                      <div className="profile-field">
                                        <span className="field-label">Can Edit Milestones</span>
                                        <span className={`field-value perm ${client.permissions?.canEditMilestones ? 'yes' : 'no'}`}>
                                          {client.permissions?.canEditMilestones ? '✓ Yes' : '✗ No'}
                                        </span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Can Edit Budgets</span>
                                        <span className={`field-value perm ${client.permissions?.canEditBudgets ? 'yes' : 'no'}`}>
                                          {client.permissions?.canEditBudgets ? '✓ Yes' : '✗ No'}
                                        </span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Storage Used</span>
                                        <span className="field-value">{formatStorage(client.storageUsed)} / {formatStorage(client.storageLimit)}</span>
                                      </div>
                                      <div className="profile-field">
                                        <span className="field-label">Status</span>
                                        <span className="field-value">{getStatusBadge(client.status)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  {client.notes && (
                                    <div className="profile-section">
                                      <h4>Internal Notes</h4>
                                      <p className="internal-notes">{client.notes}</p>
                                    </div>
                                  )}
                                </div>
                                <div className="profile-actions">
                                  <span className="member-since">Member since {formatDate(client.createdAt)}</span>
                                  <div className="profile-action-buttons">
                                    <button className="btn-edit-client" onClick={() => handleEditClient(client)}>
                                      <Edit2 size={15} />Edit Client
                                    </button>
                                  </div>
                                </div>
                              </>
                            ) : (
                              /* EDIT FORM */
                              <div className="edit-client-form">
                                <div className="edit-section">
                                  <h4>Personal Info</h4>
                                  <div className="edit-grid">
                                    <div className="edit-field">
                                      <label>Name</label>
                                      <input value={editData.displayName} onChange={e => setEditData(p => ({ ...p, displayName: e.target.value }))} />
                                    </div>
                                    <div className="edit-field">
                                      <label>Phone</label>
                                      <input value={editData.phone} onChange={e => setEditData(p => ({ ...p, phone: e.target.value }))} />
                                    </div>
                                    <div className="edit-field">
                                      <label>Address</label>
                                      <input value={editData.address} onChange={e => setEditData(p => ({ ...p, address: e.target.value }))} />
                                    </div>
                                    <div className="edit-field">
                                      <label>Website</label>
                                      <input value={editData.website} onChange={e => setEditData(p => ({ ...p, website: e.target.value }))} />
                                    </div>
                                  </div>
                                </div>
                                <div className="edit-section">
                                  <h4>Billing</h4>
                                  <div className="edit-grid">
                                    <div className="edit-field">
                                      <label>Business Name</label>
                                      <input value={editData.businessName} onChange={e => setEditData(p => ({ ...p, businessName: e.target.value }))} />
                                    </div>
                                    <div className="edit-field">
                                      <label>NIF/CIF</label>
                                      <input value={editData.nif} onChange={e => setEditData(p => ({ ...p, nif: e.target.value }))} />
                                    </div>
                                    <div className="edit-field">
                                      <label>Currency</label>
                                      <select value={editData.currency} onChange={e => setEditData(p => ({ ...p, currency: e.target.value }))}>
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                        <option value="GBP">GBP</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                                <div className="edit-section">
                                  <h4>Permissions & Status</h4>
                                  <div className="edit-grid">
                                    <div className="edit-field">
                                      <label>Status</label>
                                      <select value={editData.status} onChange={e => setEditData(p => ({ ...p, status: e.target.value }))}>
                                        <option value="approved">Approved</option>
                                        <option value="pending">Pending</option>
                                      </select>
                                    </div>
                                    <div className="edit-field">
                                      <label>Storage Limit</label>
                                      <select value={editData.storageLimit} onChange={e => setEditData(p => ({ ...p, storageLimit: e.target.value }))}>
                                        <option value={STORAGE_LIMITS['500MB']}>500 MB</option>
                                        <option value={STORAGE_LIMITS['1GB']}>1 GB</option>
                                        <option value={STORAGE_LIMITS['2GB']}>2 GB</option>
                                        <option value={STORAGE_LIMITS['5GB']}>5 GB</option>
                                      </select>
                                    </div>
                                    <div className="edit-field checkbox-field">
                                      <label>
                                        <input
                                          type="checkbox"
                                          checked={editData.permissions.canEditMilestones}
                                          onChange={e => setEditData(p => ({ ...p, permissions: { ...p.permissions, canEditMilestones: e.target.checked } }))}
                                        />
                                        Can Edit Milestones
                                      </label>
                                    </div>
                                    <div className="edit-field checkbox-field">
                                      <label>
                                        <input
                                          type="checkbox"
                                          checked={editData.permissions.canEditBudgets}
                                          onChange={e => setEditData(p => ({ ...p, permissions: { ...p.permissions, canEditBudgets: e.target.checked } }))}
                                        />
                                        Can Edit Budgets
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                <div className="edit-section">
                                  <h4>Internal Notes</h4>
                                  <textarea
                                    value={editData.notes}
                                    onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))}
                                    placeholder="Notes only visible to admin..."
                                    rows={3}
                                    className="notes-textarea"
                                  />
                                </div>
                                <div className="edit-section danger-zone">
                                  <h4><AlertCircle size={15} />Danger Zone</h4>
                                  <div className="danger-zone-actions">
                                    <div className="danger-item">
                                      <div>
                                        <span className="danger-label">Clear Chat</span>
                                        <span className="danger-desc">Delete all messages with this client</span>
                                      </div>
                                      <button className="btn-danger" onClick={() => handleClearChat(client.id)}>
                                        <Trash2 size={14} />Clear Chat
                                      </button>
                                    </div>
                                    <div className="danger-item">
                                      <div>
                                        <span className="danger-label">Delete Client</span>
                                        <span className="danger-desc">Permanently delete client and all their data</span>
                                      </div>
                                      <button className="btn-danger" onClick={() => handleDeleteClient(client)}>
                                        <Trash2 size={14} />Delete Client
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div className="edit-form-actions">
                                  <button className="btn-cancel-edit" onClick={() => setEditingClient(null)}>
                                    <X size={15} />Cancel
                                  </button>
                                  <button className="btn-save-edit" onClick={() => handleSaveClient(client.id)} disabled={saving}>
                                    {saving ? <Loader size={15} className="spinner" /> : <Save size={15} />}
                                    {saving ? 'Saving...' : 'Save Changes'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ACTIVITY TAB */}
                        {tab === 'activity' && (
                          <div className="activity-tab">
                            {/* Budget Requests */}
                            <div className="activity-section">
                              <h4 className="section-toggle" onClick={() => toggleSection(client.id, 'budget')}>
                                <FileText size={16} />Budget Requests
                                {details?.budgetRequests?.length > 0 && <span className="section-count-badge">{details.budgetRequests.length}</span>}
                                <span className="toggle-arrow">{isSectionOpen(client.id, 'budget') ? '▲' : '▼'}</span>
                              </h4>
                              {isSectionOpen(client.id, 'budget') && (!details?.budgetRequests?.length ? (
                                <p className="empty-text">No budget requests</p>
                              ) : (
                                <div className="requests-list">
                                  {details.budgetRequests.map(req => (
                                    <div key={req.id} className="request-row">
                                      <div className="request-info" style={{cursor:'pointer', flex:1}} onClick={() => setViewingRequest(req)}>
                                        <div className="request-name">{req.name}</div>
                                        <div className="request-meta">
                                          {getBudgetStatusBadge(req.status)}
                                          <span>{formatDate(req.createdAt)}</span>
                                        </div>
                                      </div>
                                      <div className="request-actions">
                                        {req.status === 'pending' && (
                                          <>
                                            <button
                                              className="btn-req-action approve"
                                              onClick={() => handleCreateProjectFromRequest(client, req)}
                                            >
                                              <Plus size={13} />Create Project
                                            </button>
                                            <button
                                              className="btn-req-action reject"
                                              onClick={() => setRejectModal({ clientId: client.id, requestId: req.id })}
                                            >
                                              <X size={13} />Reject
                                            </button>
                                          </>
                                        )}
                                        <button
                                          className="btn-req-action delete"
                                          onClick={() => handleDeleteBudgetRequest(client.id, req.id)}
                                          title="Delete request"
                                        >
                                          <Trash2 size={13} />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>

                            {/* Projects */}
                            <div className="activity-section">
                              <h4 className="section-toggle" onClick={() => toggleSection(client.id, 'projects')}>
                                <FolderOpen size={16} />Projects
                                {details?.projects?.length > 0 && <span className="section-count-badge">{details.projects.length}</span>}
                                <span className="toggle-arrow">{isSectionOpen(client.id, 'projects') ? '▲' : '▼'}</span>
                              </h4>
                              {isSectionOpen(client.id, 'projects') && (!details?.projects?.length ? (
                                <p className="empty-text">No projects yet</p>
                              ) : (
                                <div className="projects-list">
                                  {details.projects.map(project => {
                                    const pTotal = project.totalMilestones || 0;
                                    const pDone = project.completedMilestones || 0;
                                    const pPct = pTotal > 0 ? Math.round((pDone / pTotal) * 100) : 0;
                                    return (
                                      <div key={project.id} className="project-row">
                                        <div className="project-row-info">
                                          <span className="project-row-name">{project.name}</span>
                                          <div className="project-row-meta">
                                            <span className={`project-status-badge ${project.status}`}>
                                              {project.status === 'active' ? '🟢 Active' :
                                               project.status === 'completed' ? '✅ Completed' :
                                               project.status === 'paid' ? '💳 Paid' : project.status}
                                            </span>
                                            <span>{formatDate(project.createdAt)}</span>
                                            {project.budget && <span>€{project.budget}</span>}
                                            {pTotal > 0 && (
                                              <span className="project-progress-mini">
                                                <span className="progress-bar-mini">
                                                  <span className="progress-fill-mini" style={{width:`${pPct}%`}} />
                                                </span>
                                                {pDone}/{pTotal}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>

                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="modal-content reject-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Request</h3>
              <button onClick={() => setRejectModal(null)} className="btn-close"><X size={18} /></button>
            </div>
            <div className="modal-body">
              <label>Reason (optional)</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Explain why the request was rejected..."
                rows={3}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-cancel-edit" onClick={() => setRejectModal(null)}>Cancel</button>
              <button
                className="btn-reject-confirm"
                onClick={() => handleBudgetRequestAction(rejectModal.clientId, rejectModal.requestId, 'rejected', rejectReason)}
              >
                <X size={15} />Reject Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {createProjectModal && (
        <div className="modal-overlay" onClick={() => setCreateProjectModal(null)}>
          <div className="modal-content create-project-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Project for {createProjectModal.client.displayName}</h3>
              <button onClick={() => setCreateProjectModal(null)} className="btn-close"><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="edit-field">
                <label>Project Name</label>
                <input value={newProjectData.name} onChange={e => setNewProjectData(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="edit-field">
                <label>Description</label>
                <textarea value={newProjectData.description} onChange={e => setNewProjectData(p => ({ ...p, description: e.target.value }))} rows={3} />
              </div>
              <div className="edit-field">
                <label>Budget (€)</label>
                <input type="number" value={newProjectData.budget} onChange={e => setNewProjectData(p => ({ ...p, budget: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="edit-field" style={{ flex: 1 }}>
                  <label>Start Date</label>
                  <input type="date" value={newProjectData.startDate} onChange={e => setNewProjectData(p => ({ ...p, startDate: e.target.value }))} />
                </div>
                <div className="edit-field" style={{ flex: 1 }}>
                  <label>End Date</label>
                  <input type="date" value={newProjectData.endDate} onChange={e => setNewProjectData(p => ({ ...p, endDate: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel-edit" onClick={() => setCreateProjectModal(null)}>Cancel</button>
              <button className="btn-save-edit" onClick={confirmCreateProject} disabled={saving}>
                {saving ? <Loader size={15} className="spinner" /> : <Plus size={15} />}
                {saving ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Budget Request Detail Modal */}
      {viewingRequest && (
        <div className="modal-overlay" onClick={() => setViewingRequest(null)}>
          <div className="modal-content" style={{maxWidth: 480}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Budget Request Detail</h3>
              <button onClick={() => setViewingRequest(null)} className="btn-close"><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="edit-field"><label>Name</label><p style={{margin:0,padding:'8px 0'}}>{viewingRequest.name || '—'}</p></div>
              {viewingRequest.description && <div className="edit-field"><label>Description</label><p style={{margin:0,padding:'8px 0',whiteSpace:'pre-wrap'}}>{viewingRequest.description}</p></div>}
              <div style={{display:'flex',gap:12}}>
                {viewingRequest.budget && <div className="edit-field" style={{flex:1}}><label>Budget</label><p style={{margin:0,padding:'8px 0'}}>€{viewingRequest.budget}</p></div>}
                {viewingRequest.deadline && <div className="edit-field" style={{flex:1}}><label>Deadline</label><p style={{margin:0,padding:'8px 0'}}>{formatDate(viewingRequest.deadline)}</p></div>}
              </div>
              <div style={{display:'flex',gap:12}}>
                <div className="edit-field" style={{flex:1}}><label>Status</label><p style={{margin:0,padding:'8px 0'}}>{getBudgetStatusBadge(viewingRequest.status)}</p></div>
                <div className="edit-field" style={{flex:1}}><label>Submitted</label><p style={{margin:0,padding:'8px 0'}}>{formatDate(viewingRequest.createdAt)}</p></div>
              </div>
              {viewingRequest.rejectionReason && <div className="edit-field"><label>Rejection Reason</label><p style={{margin:0,padding:'8px 0',color:'#dc2626'}}>{viewingRequest.rejectionReason}</p></div>}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel-edit" onClick={() => setViewingRequest(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* New Request Alert Modal */}
      {newRequestAlert && (
        <div className="modal-overlay" onClick={() => setNewRequestAlert(null)}>
          <div className="modal-content" style={{maxWidth:400,textAlign:'center'}} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{justifyContent:'center',borderBottom:'none',paddingBottom:0}}>
              <h3 style={{fontSize:18}}>🔔 New {newRequestAlert.type === 'budget' ? 'Budget Request' : 'Project'}</h3>
            </div>
            <div className="modal-body" style={{paddingTop:8}}>
              <p style={{fontSize:15,color:'#374151',margin:0}}>
                <strong>{newRequestAlert.clientName}</strong> has submitted a new {newRequestAlert.type === 'budget' ? 'budget request' : 'project'}:
              </p>
              <p style={{fontSize:16,fontWeight:600,color:'#1d4ed8',margin:'12px 0 0'}}>{newRequestAlert.name}</p>
            </div>
            <div className="modal-footer" style={{justifyContent:'center'}}>
              <button className="btn-save-edit" onClick={() => setNewRequestAlert(null)}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsManager;
